import type {
  DictsysGermanVerbInflectionScheme,
  DictsysInflectionSchemeGermanVerbMood,
  DictsysInflectionSchemeTable
} from "@/types";

import { getBodyCellWithAuxiliaryVerbs } from "./dictsys-inflection-scheme.helper";

function getIndicativeTable(verb: DictsysInflectionSchemeGermanVerbMood): DictsysInflectionSchemeTable {
  return {
    header: "Indikativ",
    separatorIndex: 3,
    cells: [
      [
        { type: "sub-header", elements: [] },
        { type: "sub-header", elements: [{ texts: ["Præsens"], comment: "(nutid)" }] },
        { type: "sub-header", elements: [{ texts: ["Præteritum"], comment: "(datid)" }] },
        { type: "sub-header", elements: [{ texts: ["Perfektum"], comment: "(førnutid)" }] },
        { type: "sub-header", elements: [{ texts: ["Pluskvamperfektum"], comment: "(førdatid)" }] }
      ],
      [
        { type: "sub-header", elements: [{ texts: ["ich"] }] },
        getBodyCellWithAuxiliaryVerbs(verb.indicative.present.first_person_singular),
        getBodyCellWithAuxiliaryVerbs(verb.indicative.simple_past.first_person_singular),
        getBodyCellWithAuxiliaryVerbs(verb.perfect_tenses.present_perfect.first_person_singular),
        getBodyCellWithAuxiliaryVerbs(verb.perfect_tenses.past_perfect.first_person_singular)
      ],
      [
        { type: "sub-header", elements: [{ texts: ["du"] }] },
        getBodyCellWithAuxiliaryVerbs(verb.indicative.present.second_person_singular),
        getBodyCellWithAuxiliaryVerbs(verb.indicative.simple_past.second_person_singular),
        getBodyCellWithAuxiliaryVerbs(verb.perfect_tenses.present_perfect.second_person_singular),
        getBodyCellWithAuxiliaryVerbs(verb.perfect_tenses.past_perfect.second_person_singular)
      ],
      [
        { type: "sub-header", elements: [{ texts: ["er/sie/es"] }] },
        getBodyCellWithAuxiliaryVerbs([
          ...verb.indicative.present.third_person_he_singular,
          ...verb.indicative.present.third_person_she_singular,
          ...verb.indicative.present.third_person_it_singular
        ]),
        getBodyCellWithAuxiliaryVerbs([
          ...verb.indicative.simple_past.third_person_he_singular,
          ...verb.indicative.simple_past.third_person_she_singular,
          ...verb.indicative.simple_past.third_person_it_singular
        ]),
        getBodyCellWithAuxiliaryVerbs([
          ...verb.perfect_tenses.present_perfect.third_person_he_singular,
          ...verb.perfect_tenses.present_perfect.third_person_she_singular,
          ...verb.perfect_tenses.present_perfect.third_person_it_singular
        ]),
        getBodyCellWithAuxiliaryVerbs([
          ...verb.perfect_tenses.past_perfect.third_person_he_singular,
          ...verb.perfect_tenses.past_perfect.third_person_she_singular,
          ...verb.perfect_tenses.past_perfect.third_person_it_singular
        ])
      ],
      [
        { type: "sub-header", elements: [{ texts: ["wir"] }] },
        getBodyCellWithAuxiliaryVerbs(verb.indicative.present.first_person_plural),
        getBodyCellWithAuxiliaryVerbs(verb.indicative.simple_past.first_person_plural),
        getBodyCellWithAuxiliaryVerbs(verb.perfect_tenses.present_perfect.first_person_plural),
        getBodyCellWithAuxiliaryVerbs(verb.perfect_tenses.past_perfect.first_person_plural)
      ],
      [
        { type: "sub-header", elements: [{ texts: ["ihr"] }] },
        getBodyCellWithAuxiliaryVerbs(verb.indicative.present.second_person_plural),
        getBodyCellWithAuxiliaryVerbs(verb.indicative.simple_past.second_person_plural),
        getBodyCellWithAuxiliaryVerbs(verb.perfect_tenses.present_perfect.second_person_plural),
        getBodyCellWithAuxiliaryVerbs(verb.perfect_tenses.past_perfect.second_person_plural)
      ],
      [
        { type: "sub-header", elements: [{ texts: ["sie/Sie"] }] },
        getBodyCellWithAuxiliaryVerbs(verb.indicative.present.third_person_plural),
        getBodyCellWithAuxiliaryVerbs(verb.indicative.simple_past.third_person_plural),
        getBodyCellWithAuxiliaryVerbs(verb.perfect_tenses.present_perfect.third_person_plural),
        getBodyCellWithAuxiliaryVerbs(verb.perfect_tenses.past_perfect.third_person_plural)
      ]
    ]
  };
}

function getSubjunctiveTable(verb: DictsysInflectionSchemeGermanVerbMood): DictsysInflectionSchemeTable {
  return {
    header: "Konjunktiv",
    separatorIndex: 3,
    cells: [
      [
        { type: "sub-header", elements: [] },
        { type: "sub-header", elements: [{ texts: ["Konjunktiv I"] }] },
        { type: "sub-header", elements: [{ texts: ["Konjunktiv II"] }] }
      ],
      [
        { type: "sub-header", elements: [{ texts: ["ich"] }] },
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.present.first_person_singular),
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.past.first_person_singular)
      ],
      [
        { type: "sub-header", elements: [{ texts: ["du"] }] },
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.present.second_person_singular),
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.past.second_person_singular)
      ],
      [
        { type: "sub-header", elements: [{ texts: ["er/sie/es"] }] },
        getBodyCellWithAuxiliaryVerbs([
          ...verb.subjunctive.present.third_person_he_singular,
          ...verb.subjunctive.present.third_person_she_singular,
          ...verb.subjunctive.present.third_person_it_singular
        ]),
        getBodyCellWithAuxiliaryVerbs([
          ...verb.subjunctive.past.third_person_he_singular,
          ...verb.subjunctive.past.third_person_she_singular,
          ...verb.subjunctive.past.third_person_it_singular
        ])
      ],
      [
        { type: "sub-header", elements: [{ texts: ["wir"] }] },
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.present.first_person_plural),
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.past.first_person_plural)
      ],
      [
        { type: "sub-header", elements: [{ texts: ["ihr"] }] },
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.present.second_person_plural),
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.past.second_person_plural)
      ],
      [
        { type: "sub-header", elements: [{ texts: ["sie/Sie"] }] },
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.present.third_person_plural),
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.past.third_person_plural)
      ]
    ]
  };
}

function getImperativeTable(verb: DictsysInflectionSchemeGermanVerbMood): DictsysInflectionSchemeTable {
  return {
    header: "Imperativ",
    comment: "(bydemåde)",
    separatorIndex: 3,
    cells: [
      [
        { type: "sub-header", elements: [] },
        { type: "sub-header", elements: [{ texts: ["Imperativ"] }] }
      ],
      [
        { type: "sub-header", elements: [{ texts: ["Singularis"], comment: "(ental)" }] },
        getBodyCellWithAuxiliaryVerbs(verb.imperative.second_person_singular)
      ],
      [
        { type: "sub-header", elements: [{ texts: ["Pluralis"], comment: "(flertal)" }] },
        getBodyCellWithAuxiliaryVerbs(verb.imperative.second_person_plural)
      ]
    ]
  };
}

function getFutureTable(verb: DictsysInflectionSchemeGermanVerbMood): DictsysInflectionSchemeTable {
  return {
    header: "Futurum",
    comment: "(fremtid)",
    separatorIndex: 3,
    cells: [
      [
        { type: "sub-header", elements: [] },
        { type: "sub-header", elements: [{ texts: ["Indikativ"] }] },
        { type: "sub-header", elements: [{ texts: ["Konjunktiv I"] }] },
        { type: "sub-header", elements: [{ texts: ["Konjunktiv II"] }] }
      ],
      [
        { type: "sub-header", elements: [{ texts: ["ich"] }] },
        getBodyCellWithAuxiliaryVerbs(verb.indicative.future.first_person_singular),
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.future.first_person_singular),
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.future.two.first_person_singular)
      ],
      [
        { type: "sub-header", elements: [{ texts: ["du"] }] },
        getBodyCellWithAuxiliaryVerbs(verb.indicative.future.second_person_singular),
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.future.second_person_singular),
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.future.two.second_person_singular)
      ],
      [
        { type: "sub-header", elements: [{ texts: ["er/sie/es"] }] },
        getBodyCellWithAuxiliaryVerbs([
          ...verb.indicative.future.third_person_he_singular,
          ...verb.indicative.future.third_person_she_singular,
          ...verb.indicative.future.third_person_it_singular
        ]),
        getBodyCellWithAuxiliaryVerbs([
          ...verb.subjunctive.future.third_person_he_singular,
          ...verb.subjunctive.future.third_person_she_singular,
          ...verb.subjunctive.future.third_person_it_singular
        ]),
        getBodyCellWithAuxiliaryVerbs([
          ...verb.subjunctive.future.two.third_person_he_singular,
          ...verb.subjunctive.future.two.third_person_she_singular,
          ...verb.subjunctive.future.two.third_person_it_singular
        ])
      ],
      [
        { type: "sub-header", elements: [{ texts: ["wir"] }] },
        getBodyCellWithAuxiliaryVerbs(verb.indicative.future.first_person_plural),
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.future.first_person_plural),
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.future.two.first_person_plural)
      ],
      [
        { type: "sub-header", elements: [{ texts: ["ihr"] }] },
        getBodyCellWithAuxiliaryVerbs(verb.indicative.future.second_person_plural),
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.future.second_person_plural),
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.future.two.second_person_plural)
      ],
      [
        { type: "sub-header", elements: [{ texts: ["sie/Sie"] }] },
        getBodyCellWithAuxiliaryVerbs(verb.indicative.future.third_person_plural),
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.future.third_person_plural),
        getBodyCellWithAuxiliaryVerbs(verb.subjunctive.future.two.third_person_plural)
      ]
    ]
  };
}

export function getHeaderTables(inflectionScheme: DictsysGermanVerbInflectionScheme): DictsysInflectionSchemeTable[] {
  if (!inflectionScheme.verb) {
    return [];
  }

  return [
    {
      cells: [
        [
          { type: "sub-header", elements: [{ texts: ["Infinitiv"], comment: "(navnemåde)" }] },
          { type: "sub-header", elements: [{ texts: ["Præsens participium"], comment: "(lang tillægsform)" }] },
          { type: "sub-header", elements: [{ texts: ["Perfektum participium"], comment: "(kort tillægsform)" }] }
        ],
        [
          getBodyCellWithAuxiliaryVerbs(inflectionScheme.verb.infinitive.present),
          getBodyCellWithAuxiliaryVerbs(inflectionScheme.verb.participle.present),
          getBodyCellWithAuxiliaryVerbs(inflectionScheme.verb.participle.past)
        ]
      ]
    }
  ];
}

export function getTables(inflectionScheme: DictsysGermanVerbInflectionScheme): DictsysInflectionSchemeTable[] {
  if (!inflectionScheme.verb) {
    return [];
  }

  return [
    getIndicativeTable(inflectionScheme.verb),
    getSubjunctiveTable(inflectionScheme.verb),
    getImperativeTable(inflectionScheme.verb),
    getFutureTable(inflectionScheme.verb)
  ];
}
